import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  query,
  doc,
  getDoc,
  getDocs,
  collection,
  addDoc,
  updateDoc,
  where
} from "firebase/firestore";


const firebaseConfig = {
  apiKey: "AIzaSyB5810-1huDZbsxGnH_KE3x1Aehjr3J4k4",
  authDomain: "dataconduit.firebaseapp.com",
  projectId: "dataconduit",
  storageBucket: "dataconduit.appspot.com",
  messagingSenderId: "39956770044",
  appId: "1:39956770044:web:0196c88c80836305bdbb79",
  measurementId: "G-5B580410KJ"
};


const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Waits until Firebase auth has a current user, then resolves to that user
async function waitForAuth() {
  if (auth.currentUser) {
    return auth.currentUser;
  }
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsub();
        resolve(user);
      }
    });
  });
}


const registerWithEmailAndPassword = async (name, email, password) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    await addDoc(collection(db, "users"), {
      uid: user.uid,
      name,
      authProvider: "local",
      email,
    });
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};


const logout = () => {
  signOut(auth);
};

const getUserDoc = async () => {
  const uid = auth.currentUser.uid
  const docRef = doc(db, "users", uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data()
    return data
  } else {  
    console.log("No such document");
    return null
  }
}

// Db access
const getFunctions = async () => {
  const user = auth.currentUser;
  const uid = user.uid;
  const userDoc = await getDoc(doc(db, "users", uid));
  const userData = userDoc.data();
  const accounts = userData.accounts && userData.accounts.length > 0
    ? userData.accounts
    : userData.account
      ? [userData.account]
      : [];
  let docs = [];

  if (accounts.length === 0) return docs;

  // Run all queries in parallel
  const allSnapshots = await Promise.all(
    accounts.map(account => {
      const q = query(collection(db, "functions"), where("account", "==", account));
      return getDocs(q);
    })
  );

  allSnapshots.forEach(querySnapshot => {
    querySnapshot.forEach(gFunction => {
      const data = gFunction.data();
      data.id = gFunction.id;
      docs.push(data);
    });
  });

  return docs;
}

const updateVariables = async (gFunction, variables) => {
  const collectionRef = collection(db, "functions");
  const docRef = doc(collectionRef, gFunction);
  try {
    // Fetch current variables from Firestore
    const docSnap = await getDoc(docRef);
    let currentVars = {};
    if (docSnap.exists()) {
      currentVars = docSnap.data().variables || {};
    }
    // Merge: update only the keys present in the new variables, keep others
    const mergedVars = { ...currentVars, ...variables };
    await updateDoc(docRef, {
      variables: mergedVars
    });
    console.log(`Document ${gFunction} updated successfully: `, JSON.stringify(mergedVars));
  } catch (error) {
    console.error('Error updating document: ', error);
  }
}

// Helper to generate the Cloud Run Job trigger endpoint for a given function name
// Cloud Run Proxy endpoint
const CLOUDRUN_PROXY_BASE = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:8080'
  : 'https://cloudrun-proxy-39956770044.us-east4.run.app';

async function getJobs(options) {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);
  const includeLatestExecution = options?.includeLatestExecution ?? true;
  const includeSchedule = options?.includeSchedule ?? true;

  const url = `${CLOUDRUN_PROXY_BASE}/jobs?includeLatestExecution=${includeLatestExecution}&includeSchedule=${includeSchedule}`;
  const init = {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
  };
  if (options?.signal) {
    init.signal = options.signal;
  }
  const response = await fetch(url, init);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch jobs: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.jobs || [];
}

async function getJobTemplates() {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);

  const response = await fetch(`${CLOUDRUN_PROXY_BASE}/job-templates`, {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch job templates: ${response.statusText}`);
  }
  const data = await response.json();
  return data.templates || [];
}

async function getDcoResults(options = {}) {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);
  const params = new URLSearchParams();
  if (options.account) params.set('account', options.account);
  if (options.since) params.set('since', options.since.toISOString());
  if (options.limit) params.set('limit', options.limit);
  const url = `${CLOUDRUN_PROXY_BASE}/dco/results?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch DCO results: ${response.statusText}`);
  }
  const data = await response.json();
  return data.results || [];
}

async function runJob(jobId) {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);

  // Call the proxy service, passing the Firebase ID token and functionName
  const response = await fetch(`${CLOUDRUN_PROXY_BASE}/jobs/${jobId}/run`, {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      {
        "args": ["--adhoc"]
      }
    )
  });
  if (!response.ok) {
    throw new Error(`Failed to call Cloud Run Proxy for ${jobId}: ${response.statusText}`)
  }
  return response.json();
}

async function createJob(payload) {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);

  const response = await fetch(`${CLOUDRUN_PROXY_BASE}/jobs`, {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const err = await response.json();
      msg = err.message || msg;
    } catch (_) {}
    throw new Error(`Failed to create job: ${msg}`);
  }
  return response.json();
}

async function deleteJob(jobName) {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);

  const response = await fetch(`${CLOUDRUN_PROXY_BASE}/jobs/${jobName}`, {
    method: 'DELETE',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const err = await response.json();
      msg = err.message || msg;
    } catch (_) {}
    throw new Error(`Failed to delete job: ${msg}`);
  }
  return response.json();
}

async function validateField(validationName, url) {
  const user = auth.currentUser || await waitForAuth();
  const idToken = await user.getIdToken(true);

  const response = await fetch(`${CLOUDRUN_PROXY_BASE}/validations/${validationName}?url=${encodeURIComponent(url)}`, {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + idToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const err = await response.json();
      msg = err.message || msg;
    } catch (_) {}
    throw new Error(`Validation failed: ${msg}`);
  }
  return response.json();
}

export {
  auth,
  db,
  registerWithEmailAndPassword,
  updateVariables,
  logout,
  getFunctions,
  getJobs,
  getUserDoc,
  runJob,
  getJobTemplates,
  getDcoResults,
  createJob,
  deleteJob,
  validateField,
  waitForAuth
};
